// TODO: vitest component test (pages are covered by Playwright e2e)
import { useParcelExtractionEnabled } from '@/hooks/useParcelExtractionEnabled'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Package, Plus, Camera, User, Search, Check } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, SetupGuide, Skeleton, Input, Select, DataTable, Pagination, CardKebabMenu, ConfirmDialog, Lightbox } from '@/components/ui'
import { MobileSortControl } from '@/components/ui/MobileSortControl'
import type { Column } from '@/components/ui/DataTable'
import { useParcels, useCollectParcel, useDeleteParcel, useUpdateParcel } from '@/hooks/useParcels'
import { useProperties } from '@/hooks/useProperties'
import { usePropertyUnits } from '@/hooks/useUnits'
import { useListSort } from '@/hooks/useListSort'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useSelectedPropertyStore } from '@/stores'
import { getParcelDisplayStatus } from '@/lib/parcelStatus'
import type { Parcel } from '@/types'

export function ParcelListPage() {
  const { t } = useTranslation('parcels')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const formatDate = useDateFormatter()
  const parcelTextExtractionEnabled = useParcelExtractionEnabled()
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null)
  const [pendingDelete, setPendingDelete] = useState<Parcel | null>(null)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  // EP-97: default-rule page — updated_at desc, user-selectable server-side sort.
  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'updated_at',
    defaultOrder: 'desc',
  })
  const handleSort = (key: string) => { setPage(1); setSort(key) }
  const handleToggleOrder = () => { setPage(1); toggleOrder() }

  const collectParcel = useCollectParcel()
  const deleteParcel = useDeleteParcel()

  // Property scope is shared with the global header switcher (persisted in
  // localStorage), same pattern as /meter-readings. The in-page picker below
  // stays visible above the filters so the scope is obvious on this page.
  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const setSelectedPropertyId = useSelectedPropertyStore((s) => s.setSelectedPropertyId)

  const { data, isLoading, isFetching, error } = useParcels(
    page,
    status || undefined,
    selectedPropertyId ?? undefined,
    from || undefined,
    to || undefined,
    searchQuery || undefined,
    sortParams.sort,
    sortParams.sort_order,
  )
  const { data: propertiesData } = useProperties(1)

  // Second query for total count (no filters) to decide setup guide vs empty state
  const { data: totalData } = useParcels(1)
  const totalCount = totalData?.meta?.total_count ?? 0

  const parcels = data?.data || []
  const properties = propertiesData?.data || []
  const hasProperties = properties.length > 0

  // EP-67: tab-independent count from the index meta drives the review badge.
  const needsReviewCount = data?.meta?.needs_review_count ?? 0

  const statusTabs: { value: string; label: string; count?: number }[] = [
    { value: '', label: t('filters.all') },
    { value: 'pending', label: t('filters.pending') },
    { value: 'collected', label: t('filters.collected') },
    ...(parcelTextExtractionEnabled
      ? [{ value: 'needs_review', label: t('filters.needs_review'), count: needsReviewCount }]
      : []),
  ]

  const getStatusBadge = (parcel: Parcel) => {
    // EP-67: when the dual-gate is on, show the derived status (surfaces
    // "Needs review"); otherwise keep the plain lifecycle status.
    if (parcelTextExtractionEnabled) {
      const s = getParcelDisplayStatus(parcel)
      return <Badge variant={s.variant}>{t(s.labelKey, s.labelOpts)}</Badge>
    }
    switch (parcel.status) {
      case 'collected':
        return <Badge variant="success">{t('status.collected')}</Badge>
      case 'notified':
        return <Badge variant="info">{t('status.notified')}</Badge>
      case 'pending':
      default:
        return <Badge variant="warning">{t('status.pending')}</Badge>
    }
  }

  const apiBase = import.meta.env.VITE_API_URL || ''
  const thumbnailUrl = (raw: string) =>
    raw.startsWith('/api/images/') ? `${apiBase}${raw}` : `${apiBase}/api/images/${raw}`

  const buildKebabItems = (p: Parcel) => {
    const items: { label: string; onClick: () => void; variant?: 'default' | 'error' }[] = []
    if (p.status !== 'collected') {
      items.push({
        label: t('actions.mark_collected'),
        onClick: async () => { await collectParcel.mutateAsync(p.id) },
      })
    }
    items.push({
      label: tc('actions.view'),
      onClick: () => navigate(`/parcels/${p.id}`),
    })
    items.push({
      label: tc('actions.edit'),
      onClick: () => navigate(`/parcels/${p.id}/edit`),
    })
    items.push({
      label: tc('actions.delete'),
      onClick: () => setPendingDelete(p),
      variant: 'error',
    })
    return items
  }

  const columns: Column<Parcel>[] = [
    // Kebab quick actions — leftmost so admin/staff can act without
    // entering the detail page.
    {
      key: 'actions',
      header: '',
      render: (p) => (
        <div onClick={(e) => e.stopPropagation()}>
          <CardKebabMenu items={buildKebabItems(p)} ariaLabel={tc('labels.actions_menu')} />
        </div>
      ),
    },
    // Thumbnail preview — click opens a full-screen lightbox.
    {
      key: 'photo',
      header: t('labels.photos'),
      render: (p) => {
        const images = p.image_urls ?? []
        if (images.length === 0) {
          return (
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-12 h-12 rounded-md bg-base-200 flex items-center justify-center text-base-content/30 cursor-default"
              title={t('empty.title')}
            >
              <Package className="w-5 h-5" />
            </div>
          )
        }
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setLightbox({ images, index: 0 })
            }}
            className="relative block focus:outline-none focus:ring-2 focus:ring-primary rounded-md"
            aria-label={t('labels.photos')}
          >
            <img
              src={thumbnailUrl(images[0])}
              alt=""
              className="w-12 h-12 rounded-md object-cover border border-base-200 hover:opacity-80 transition-opacity"
              loading="lazy"
            />
            {images.length > 1 && (
              <span className="absolute -bottom-1 -right-1 bg-base-content/80 text-base-100 text-[10px] leading-none px-1 py-0.5 rounded">
                +{images.length - 1}
              </span>
            )}
          </button>
        )
      },
    },
    // Recipient (composite: recipient_name · #unit · customer) — the
    // identifying text column.
    {
      key: 'customer',
      header: t('labels.recipient'),
      render: (p) => {
        const recipient = p.recipient_name?.trim()
        const unitNumber = p.unit?.number
        const customerName = p.customer?.name
        if (!recipient && !unitNumber && !customerName) return '-'
        return (
          <div className="flex flex-col gap-0.5 min-w-0">
            {recipient && (
              <div className="flex items-center gap-1 font-medium truncate">
                <User className="w-3 h-3 text-base-content/40 shrink-0" />
                <span className="truncate">{recipient}</span>
              </div>
            )}
            <div className="text-xs text-base-content/60 truncate">
              {[
                unitNumber ? `#${unitNumber}` : null,
                customerName,
              ].filter(Boolean).join(' · ') || (recipient ? '' : '-')}
            </div>
          </div>
        )
      },
    },
    {
      key: 'tracking_number',
      header: t('labels.tracking_number'),
      hideBelow: 'md',
      render: (p) => <span className="font-medium truncate max-w-[160px] block">{p.tracking_number || '-'}</span>,
    },
    {
      key: 'carrier',
      header: t('labels.carrier'),
      hideBelow: 'md',
      render: (p) => p.carrier || '-',
    },
    {
      key: 'status',
      header: t('labels.status'),
      render: (p) => getStatusBadge(p),
    },
    {
      key: 'received_at',
      header: t('labels.received_at'),
      sortable: true,
      hideBelow: 'lg',
      render: (p) => formatDate(p.received_at),
    },
    {
      key: 'collected_at',
      header: t('labels.collected_at'),
      sortable: true,
      hideBelow: 'lg',
      render: (p) => (p.collected_at ? formatDate(p.collected_at) : '-'),
    },
    // EP-67: inline batch-review resolver — only on the "Needs review" tab.
    ...(parcelTextExtractionEnabled && status === 'needs_review'
      ? [{
          key: 'resolve',
          header: t('actions.assign_unit'),
          render: (p: Parcel) => <InlineUnitResolver parcel={p} />,
        } as Column<Parcel>]
      : []),
  ]

  // EP-97: mobile sort options mirror the allow-listed sortable columns. Labels
  // reuse the column-header translations; the default updated_at gets a "Last updated"
  // label since it has no visible column.
  const sortOptions = [
    { value: 'updated_at', label: tc('sort.by_last_updated') },
    { value: 'received_at', label: t('labels.received_at') },
    { value: 'collected_at', label: t('labels.collected_at') },
  ]

  const activeFilterCount = [status, from, to].filter(Boolean).length

  return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: t('title') }]} />

        <PageHeader
          title={t('title')}
          isRefreshing={isFetching && !isLoading}
          titleAction={
            <Select
              data-testid="page-property-picker-mobile"
              aria-label={tc('labels.select_property')}
              value={selectedPropertyId ? String(selectedPropertyId) : ''}
              onChange={(e) => {
                setSelectedPropertyId(e.target.value ? Number(e.target.value) : null)
                setPage(1)
              }}
              options={[
                { value: '', label: tc('labels.all_properties') },
                ...properties.map((p) => ({ value: String(p.id), label: p.name })),
              ]}
              className="max-w-[9rem]"
              size="sm"
            />
          }
          primaryAction={{
            key: 'new-parcel',
            label: t('new_parcel'),
            icon: Plus,
            to: '/parcels/new',
          }}
          overflowActions={[
            {
              key: 'scan',
              label: t('scan.quick_add'),
              icon: Camera,
              to: '/parcels/scan',
              hidden: !parcelTextExtractionEnabled,
              testId: 'header-scan-parcel',
            },
          ]}
        />

        {/* Filters */}
        <Card>
          {/* Search — always visible */}
          <div className="w-full min-w-0">
            <Input
              label={tc('actions.search')}
              leftIcon={<Search className="w-4 h-4" />}
              placeholder={t('filters.search_placeholder')}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1) }}
            />
          </div>

          {/* Mobile-only toggle */}
          <button
            type="button"
            className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium mt-3"
            aria-expanded={mobileFiltersOpen}
            aria-controls="parcel-filters-body"
            onClick={() => setMobileFiltersOpen((v) => !v)}
          >
            <span>
              {tc('filters.toggle')}
              {activeFilterCount > 0 && (
                <Badge variant="count" size="sm" className="ml-2">{activeFilterCount}</Badge>
              )}
            </span>
            <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
          </button>

          <div
            id="parcel-filters-body"
            className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
          >
            {/* EP-97: mobile sort control — desktop sorts via clickable table headers. */}
            <div className="w-full sm:hidden">
              <MobileSortControl
                options={sortOptions}
                value={sort}
                order={order}
                onChange={handleSort}
                onToggleOrder={handleToggleOrder}
              />
            </div>
            {/* Status Tabs */}
            <div className="flex gap-1 self-center overflow-x-auto">
              {statusTabs.map((tab) => (
                <Button
                  key={tab.value}
                  variant={status === tab.value ? 'primary' : 'ghost'}
                  size="sm"
                  className="flex-shrink-0 whitespace-nowrap"
                  onClick={() => {
                    setStatus(tab.value)
                    setPage(1)
                  }}
                  data-testid={tab.value === 'needs_review' ? 'tab-needs-review' : undefined}
                >
                  {tab.label}
                  {tab.count != null && tab.count > 0 && (
                    <Badge variant="error" size="sm" className="ml-1.5">
                      <span data-testid="needs-review-count">{tab.count}</span>
                    </Badge>
                  )}
                </Button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full sm:flex-1 sm:min-w-[280px]">
              <Input
                label={t('filters.date_from')}
                type="date"
                value={from}
                onChange={(e) => { setFrom(e.target.value); setPage(1) }}
              />
              <Input
                label={t('filters.date_to')}
                type="date"
                value={to}
                onChange={(e) => { setTo(e.target.value); setPage(1) }}
              />
            </div>
          </div>
        </Card>

        {/* Parcel Table */}
        {isLoading ? (
          <Card>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          </Card>
        ) : error ? (
          <Card>
            <EmptyState
              title={tc('errors.something_went_wrong')}
              action={
                <Button onClick={() => window.location.reload()}>
                  {tc('actions.retry')}
                </Button>
              }
            />
          </Card>
        ) : !parcels.length ? (
          <Card>
            {totalCount === 0 ? (
              <SetupGuide
                icon={<Package className="w-16 h-16" />}
                title={t('setup_guide.title')}
                description={t('setup_guide.description')}
                prerequisites={[
                  {
                    label: t('setup_guide.prerequisites.customers'),
                    met: hasProperties,
                    link: '/properties',
                    linkLabel: tc('actions.setup'),
                  },
                ]}
                prerequisitesTitle={tc('labels.prerequisites')}
                steps={[
                  {
                    title: t('setup_guide.steps.register.title'),
                    description: t('setup_guide.steps.register.description'),
                  },
                  {
                    title: t('setup_guide.steps.notify.title'),
                    description: t('setup_guide.steps.notify.description'),
                  },
                  {
                    title: t('setup_guide.steps.collect.title'),
                    description: t('setup_guide.steps.collect.description'),
                  },
                ]}
                action={
                  <Button
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => navigate('/parcels/new')}
                    disabled={!hasProperties}
                  >
                    {t('new_parcel')}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={<Package className="w-12 h-12" />}
                title={t('empty.title')}
                description={t('empty.description')}
                action={
                  <Button
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => navigate('/parcels/new')}
                  >
                    {t('new_parcel')}
                  </Button>
                }
              />
            )}
          </Card>
        ) : (
          <Card>
            <DataTable
              columns={columns}
              data={parcels}
              keyExtractor={(p) => p.id}
              onRowClick={(p) => navigate(`/parcels/${p.id}`)}
              sortBy={sort}
              sortOrder={order}
              onSort={handleSort}
            />
          </Card>
        )}

        {/* Pagination */}
        {data?.meta && data.meta.total_count > 0 && (
          <div className="flex justify-center">
            <Pagination
              currentPage={data.meta.current_page}
              totalPages={data.meta.total_pages}
              totalItems={data.meta.total_count}
              pageSize={data.meta.per_page}
              onPageChange={setPage}
            />
          </div>
        )}

        {lightbox && (
          <Lightbox
            images={lightbox.images}
            index={lightbox.index}
            onIndexChange={(i) => setLightbox({ images: lightbox.images, index: i })}
            onClose={() => setLightbox(null)}
            toUrl={thumbnailUrl}
          />
        )}

        <ConfirmDialog
          isOpen={!!pendingDelete}
          onClose={() => setPendingDelete(null)}
          onConfirm={async () => {
            if (!pendingDelete) return
            await deleteParcel.mutateAsync(pendingDelete.id)
            setPendingDelete(null)
          }}
          title={tc('actions.delete')}
          message={t('messages.delete_confirm')}
          confirmLabel={tc('actions.delete')}
          cancelLabel={tc('actions.cancel')}
          variant="error"
          loading={deleteParcel.isPending}
        />
      </div>
  )
}

// ---------------------------------------------------------------------------
// EP-67 inline batch-review resolver — one per "Needs review" row.
//
// A child component so usePropertyUnits(property_id) obeys the Rules of Hooks
// (one hook instance per row). Picking a candidate or a unit + Save PATCHes
// unit_id; the parcel then drops out of the needs_review bucket and the tab
// badge decrements on the list's next refetch (useUpdateParcel evicts the list).
// ---------------------------------------------------------------------------

function InlineUnitResolver({ parcel }: { parcel: Parcel }) {
  const { t } = useTranslation('parcels')
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('parcels')
  const updateParcel = useUpdateParcel()
  const { data: unitsData } = usePropertyUnits(parcel.property_id)
  const units = unitsData?.data ?? []
  const [selectedUnitId, setSelectedUnitId] = useState('')

  const unitOptions = [
    { value: '', label: t('placeholders.select_unit') },
    ...units.map((u) => ({ value: String(u.id), label: u.number ?? u.name })),
  ]

  const candidates = parcel.extraction_candidates ?? []

  const pickCandidate = (candidate: string) => {
    const unit = units.find((u) => (u.number ?? u.name) === candidate)
    if (unit) setSelectedUnitId(String(unit.id))
  }

  const handleSave = async () => {
    if (!selectedUnitId) return
    try {
      await updateParcel.mutateAsync({ id: parcel.id, unit_id: Number(selectedUnitId) })
      addToast({ type: 'success', message: t('messages.updated') })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid={`resolve-row-${parcel.id}`}
      onClick={(e) => e.stopPropagation()}
    >
      {candidates.map((c) => (
        <Button
          key={c}
          variant="outline"
          size="xs"
          className="flex-shrink-0 whitespace-nowrap"
          data-testid={`resolve-candidate-${c}`}
          onClick={() => pickCandidate(c)}
        >
          {c}
        </Button>
      ))}
      <Select
        aria-label={t('placeholders.select_unit')}
        options={unitOptions}
        value={selectedUnitId}
        onChange={(e) => setSelectedUnitId(e.target.value)}
        size="sm"
        className="min-w-[8rem]"
      />
      <Button
        size="sm"
        leftIcon={<Check className="w-4 h-4" />}
        disabled={!selectedUnitId}
        loading={updateParcel.isPending}
        onClick={handleSave}
        data-testid={`resolve-save-${parcel.id}`}
      >
        {t('actions.assign_unit')}
      </Button>
    </div>
  )
}
